import { from, merge, of, timer, EMPTY } from 'rxjs';
import { timeout, mergeMap, catchError, repeatWhen, take, takeUntil, tap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { LogService } from '../../services/log-service';
import { pusherTimeoutForTesting } from './pusher.timeout.test-helper';

const defaultNotification = () => {
  NotificationService.showNotification({
    title: <FormattedMessage defaultMessage='Error' description='title for pusher timeout' id='/ByyxI' />,
    content: (
      <FormattedMessage
        defaultMessage='The server encountered a temporary error and could not complete your request. Please refresh the page and try again.'
        description='content for pusher timeout'
        id='dAbuTW'
      />
    ),
    notificationType: NOTIFICATION_TYPE_ERROR,
    dontHide: true,
  });
};

const getTimeout = (srcValue) => {
  return pusherTimeoutForTesting.getTimeoutMs() || srcValue;
};

/*
 * this function can be used to create an observable based timeout that you can merge with an existing observable
 * the timeout will be cancelled if the finalActionType specified is seen on the action$ stream
 * or when a custom observable, passed in here as clearTimeout$, emits a value.
 * It is possible to delay the timeout by giving it an array of action types that will be seen on the action$ stream.
 * If the timeout is triggered we will do the following: call the notificationFunc callback, log the timeout to sumo.
 * The only value emitted by this observable occurs if the timeout triggers.
 * The value is always an object that contains the result of the errorMessage$ passed in and retryCount, an integer representing the number of times the timeout has been retried.
 * To use the retry functionality pass in an observable as the retryTrigger$, whenever that observable emits a value we will resubscribe and restart the timeout.
 */
export const pusherTimeout$ = ({
  action$,
  actionTypes,
  timeoutMs = 30000,
  notificationFunc = defaultNotification,
  finalActionType,
  clearTimeout$,
  logContext = {},
  errorMessage$ = EMPTY,
  retryTrigger$ = EMPTY,
  restartTimeout$ = EMPTY,
}) => {
  let filteredAction$;
  if (finalActionType) {
    filteredAction$ = action$.pipe(ofType(...actionTypes), takeUntil(action$.pipe(ofType(finalActionType), take(1))));
  } else {
    filteredAction$ = clearTimeout$;
  }

  let retryCount = 0;
  return merge(timer(0), restartTimeout$, filteredAction$)
    .pipe(
      timeout(getTimeout(timeoutMs)),
      mergeMap(() => EMPTY),
      catchError((e) => {
        notificationFunc(e);
        LogService.logInfo(`Timed out waiting for pusher response for action type: ${finalActionType}`, {
          error: e,
          actionTypes,
          timeoutMs,
          finalActionType,
          ...logContext,
        });
        return errorMessage$;
      }),
      mergeMap((errorMessage) => {
        return of({ errorMessage, retryCount });
      }),
      repeatWhen(() => {
        return retryTrigger$.pipe(
          tap(() => {
            retryCount += 1;
          }),
        );
      }),
      takeUntil(finalActionType ? action$.pipe(ofType(finalActionType), take(1)) : clearTimeout$),
    )
    .pipe(
      mergeMap((errorMessage) => {
        // wrap this in a promise in case the decision to retry
        // is not async in nature. repeatWhen needs it to be async
        const promise = new Promise((res) => {
          res(errorMessage);
        });
        return from(promise);
      }),
    );
};
